import { getEnv } from '@/composables/useEnv'
import { useHandleError } from '@/composables/useHandleError'
import {
  SanityDataStore,
  TPerspective
} from '@/services/dataStore/SanityDataStore'
import { StaticDataStore } from '@/services/dataStore/StaticDataStore'
import { computed, ComputedRef, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ZodError } from 'zod'

export default () => {
  // 'static' or 'sanity'
  const dataStoreSource = getEnv('VITE_APP_DATA_SOURCE') ?? 'static'
  const environment = getEnv('VITE_APP_ENV')
  const route = useRoute()
  const { logError } = useHandleError()

  const perspective: ComputedRef<TPerspective> = computed(() => {
    const queryPerspective = route.query?.perspective
    if (queryPerspective === 'drafts') return 'previewDrafts'
    return 'published' // default to published
  })

  // Invalidate SWRV caches if perspective changes
  interface DataStoreSWRVKey {
    perspective: TPerspective
  }
  const dataStoreSWRVKey = ref({
    perspective: perspective.value
  } as DataStoreSWRVKey)

  watch(perspective, () => {
    dataStoreSWRVKey.value.perspective = perspective.value
  })

  // In lower environments, allow invalid draft content through to allow testing new content.
  //   Be warned - invalid draft content can break things in the UI!
  // To override this for debugging validations, updating here will flip the behavior everywhere.
  const disableValidations = computed(
    () => environment !== 'production' && perspective.value === 'previewDrafts'
  )

  const dataStore = computed(() => {
    switch (dataStoreSource) {
      case 'sanity': {
        try {
          return new SanityDataStore(
            perspective.value,
            disableValidations.value
          )
        } catch (e) {
          console.warn('Error initializing SanityDataStore', e)
        }
        // falls through if error
      }
      case 'static':
      default: {
        return new StaticDataStore()
      }
    }
  })

  const handleDataStoreError = (e: unknown) => {
    if (e instanceof ZodError) {
      console.warn('Invalid item. Returning empty...')
      console.debug('Validation error: ', e)
      logError(e, 'Sanity item is invalid.')
    } else if (!(e as any).isNetworkError) {
      logError(e, 'Error fetching from Sanity')
    } else {
      console.warn('Error fetching from Sanity: ', e)
    }
  }

  return {
    perspective,
    dataStore,
    handleDataStoreError,
    disableValidations,
    dataStoreSWRVKey
  }
}
